import { useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import axios from "axios";
import heroMap from "./assets/hero-map.jpg";
import {
  FileText,
  PlayCircle,
  Headphones,
  MessageCircle,
  Landmark,
  GraduationCap,
  ArrowUpRight,
  Search,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  ExternalLink,
  Check,
  ChevronDown,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const BOOK_URL =
  "https://books.friesenpress.com/store/title/119734000577849371/Charlie-Dickson-What-Canadians-Said-in-2025";
const BOOK_TITLE = "What Canadians Said in 2025";
const BOOK_AUTHOR = "Charlie Dickson";

const SOURCE_ICON = {
  article: FileText,
  video: PlayCircle,
  podcast: Headphones,
  twitter_x: MessageCircle,
  government: Landmark,
  academic: GraduationCap,
};

const SOURCE_LABEL = {
  article: "Article",
  video: "Video",
  podcast: "Podcast",
  twitter_x: "Social",
  government: "Gov / Hansard",
  academic: "Academic",
};

// -------------------- Utilities --------------------
const authorSlug = (name) =>
  name
    .toLowerCase()
    .replace(/['"’.]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-CA", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return null;
  }
};

// -------------------- Book CTA (used in nav + hero) --------------------
const BookCTA = ({ variant = "nav", "data-testid": testId }) => {
  const cls =
    variant === "nav"
      ? "clay-btn clay-btn-accent !py-2 !px-3.5 md:!px-4 md:!py-2.5 text-[10px] md:text-[11px]"
      : variant === "hero-primary"
        ? "clay-btn clay-btn-primary"
        : variant === "hero-secondary"
          ? "clay-btn clay-btn-secondary"
          : "clay-btn clay-btn-accent";
  return (
    <a
      href={BOOK_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={cls}
      data-testid={testId || `book-cta-${variant}`}
    >
      <BookOpen size={13} strokeWidth={1.75} />
      <span className="hidden sm:inline">Buy the book</span>
      <span className="sm:hidden">Book</span>
      <ArrowUpRight size={11} strokeWidth={1.75} />
    </a>
  );
};

// -------------------- Header --------------------
const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl border-b"
      style={{ background: "rgba(250,249,245,0.9)", borderColor: "var(--border-subtle)" }}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between gap-4">
        <button onClick={() => navigate("/")} className="text-left min-w-0 shrink" data-testid="header-home-link">
          <div className="font-mono text-[9px] md:text-[10px] tracking-archive uppercase" style={{ color: "var(--text-tertiary)" }}>
            Vol. I · MMXXV
          </div>
          <div
            className="font-serif text-lg md:text-2xl leading-none mt-0.5 md:mt-1 truncate"
            style={{ letterSpacing: "-0.02em" }}
          >
            The 2025 Canada Index
          </div>
        </button>
        <nav className="hidden lg:flex items-center gap-8 font-mono text-xs uppercase tracking-archive">
          <Link to="/" className={`nav-link ${location.pathname === "/" ? "active" : ""}`} data-testid="nav-home">
            Front Matter
          </Link>
          <Link
            to="/archive"
            className={`nav-link ${
              location.pathname === "/archive" || location.pathname.startsWith("/category") ? "active" : ""
            }`}
            data-testid="nav-archive"
          >
            By Theme
          </Link>
          <Link
            to="/authors"
            className={`nav-link ${location.pathname.startsWith("/author") ? "active" : ""}`}
            data-testid="nav-authors"
          >
            By Author
          </Link>
          <Link
            to="/about"
            className={`nav-link ${location.pathname === "/about" ? "active" : ""}`}
            data-testid="nav-about"
          >
            Colophon
          </Link>
        </nav>
        <BookCTA variant="nav" data-testid="header-book-cta" />
      </div>
    </header>
  );
};

// -------------------- Footer --------------------
const Footer = ({ stats }) => (
  <footer className="border-t mt-32" style={{ borderColor: "var(--border-subtle)" }} data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-archive" style={{ color: "var(--text-tertiary)" }}>
            Vol. I · MMXXV
          </div>
          <h3 className="font-serif text-3xl mt-2 leading-tight" style={{ letterSpacing: "-0.02em" }}>
            The 2025 Canada Index
          </h3>
          <p className="mt-4 text-sm" style={{ color: "var(--text-secondary)" }}>
            The working bibliography that accompanies{" "}
            <a
              href={BOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="italic underline underline-offset-2"
              style={{ color: "var(--text-primary)" }}
            >
              {BOOK_TITLE}
            </a>{" "}
            by {BOOK_AUTHOR}.
          </p>
          <div className="mt-6">
            <BookCTA variant="hero-primary" data-testid="footer-book-cta" />
          </div>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-archive mb-4" style={{ color: "var(--text-tertiary)" }}>
            By the Numbers
          </div>
          <ul className="space-y-2 font-mono text-xs">
            <li className="flex justify-between border-b py-2" style={{ borderColor: "var(--border-subtle)" }}>
              <span>Entries</span>
              <span>{stats?.total_entries ?? "—"}</span>
            </li>
            <li className="flex justify-between border-b py-2" style={{ borderColor: "var(--border-subtle)" }}>
              <span>Themes</span>
              <span>{stats?.total_categories ?? "—"}</span>
            </li>
            <li className="flex justify-between border-b py-2" style={{ borderColor: "var(--border-subtle)" }}>
              <span>Distinct Voices</span>
              <span>{stats?.unique_voices ?? "—"}</span>
            </li>
            <li className="flex justify-between py-2">
              <span>Year of Record</span>
              <span>{stats?.year ?? "2025"}</span>
            </li>
          </ul>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-archive mb-4" style={{ color: "var(--text-tertiary)" }}>
            Methodology
          </div>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            Each entry links to the original source. Where the outlet permits, we preview the piece
            with its own headline, description, and hero image. Inclusion is not endorsement.
            Categorisation is editorial.
          </p>
        </div>
      </div>
      <div className="mt-16 pt-8 border-t flex flex-col md:flex-row justify-between items-start md:items-center gap-4 font-mono text-[11px] uppercase tracking-archive" style={{ borderColor: "var(--border-subtle)", color: "var(--text-tertiary)" }}>
        <span className="max-w-2xl">
          © {new Date().getFullYear()} the writers, broadcasters, witnesses and officials cited herein · All rights reserved to their respective authors
        </span>
        <span className="shrink-0">
          Developed & maintained by{" "}
          <a
            href="https://aikarotech.com"
            target="_blank"
            rel="noopener noreferrer"
            className="aikaro-link"
            data-testid="footer-aikarotech-link"
          >
            Aikarotech
          </a>
        </span>
      </div>
    </div>
  </footer>
);

// -------------------- ClayDropdown --------------------
// Elegant paper-card dropdown: single control, checkmark on selected, click-outside + esc to close.
const ClayDropdown = ({
  label,
  value,
  onChange,
  options, // [{ value, label, count? }]
  placeholder = "All",
  testId,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const active = value !== "" && value != null;
  const selected = options.find((o) => o.value === value);
  const displayLabel = active
    ? selected?.label || value
    : placeholder;

  return (
    <div ref={wrapRef} className="relative inline-block" data-testid={testId ? `${testId}-wrap` : undefined}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        className={`clay-dd-trigger ${active ? "active" : ""}`}
        data-testid={testId}
      >
        <span
          className="truncate max-w-[16rem] md:max-w-[22rem]"
          style={{ display: "inline-block", verticalAlign: "middle" }}
        >
          {label && (
            <span style={{ opacity: 0.55, marginRight: "0.5rem" }}>
              {label}:
            </span>
          )}
          {displayLabel}
        </span>
        <ChevronDown size={12} strokeWidth={2} className="clay-dd-caret" />
      </button>
      {open && (
        <div role="listbox" className="clay-dd-menu" data-testid={testId ? `${testId}-menu` : undefined}>
          <button
            type="button"
            role="option"
            aria-selected={!active}
            className={`clay-dd-item ${!active ? "selected" : ""}`}
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            data-testid={testId ? `${testId}-option-all` : undefined}
          >
            <span className="clay-dd-check">
              {!active && <Check size={12} strokeWidth={2.25} />}
            </span>
            <span>{placeholder}</span>
          </button>
          {options.map((o) => {
            const isSel = o.value === value;
            return (
              <button
                type="button"
                role="option"
                aria-selected={isSel}
                key={o.value}
                className={`clay-dd-item ${isSel ? "selected" : ""}`}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                data-testid={testId ? `${testId}-option-${o.value}` : undefined}
              >
                <span className="clay-dd-check">
                  {isSel && <Check size={12} strokeWidth={2.25} />}
                </span>
                <span className="truncate">{o.label}</span>
                {o.count != null && <span className="clay-dd-count">{o.count}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// -------------------- Entry Row (list view — clay spine) --------------------
const EntryRow = ({ entry, index, showCategory = false }) => {
  const Icon = SOURCE_ICON[entry.source_type] || FileText;
  const displayTitle = entry.title || `${entry.author} · ${entry.outlet_name || entry.domain}`;
  return (
    <Link to={`/entry/${entry.id}`} className="spine-card mb-3" data-testid={`entry-row-${entry.id}`}>
      <div className="flex items-start md:items-center justify-between gap-6">
        <div className="flex items-start gap-4 md:gap-6 flex-1 min-w-0">
          <div className="font-mono text-[10px] tracking-archive pt-1 hidden sm:block" style={{ color: "var(--text-tertiary)" }}>
            {String(index + 1).padStart(3, "0")}
          </div>
          <div className="flex-1 min-w-0">
            <div className="spine-title text-xl md:text-[24px]">{displayTitle}</div>
            <div className="mt-1.5 text-sm" style={{ color: "var(--text-secondary)" }}>
              <span className="italic">{entry.author}</span>
              {showCategory && (
                <>
                  {" · "}
                  <span>{entry.category}</span>
                </>
              )}
            </div>
            <div className="mt-2 flex items-center gap-2.5 text-[11px] font-mono flex-wrap" style={{ color: "var(--text-secondary)" }}>
              <Icon size={12} strokeWidth={1.5} />
              <span className="uppercase tracking-archive">{SOURCE_LABEL[entry.source_type]}</span>
              <span style={{ color: "var(--text-tertiary)" }}>·</span>
              <span className="truncate">{entry.outlet_name || entry.domain}</span>
            </div>
          </div>
        </div>
        <ArrowUpRight
          size={22}
          strokeWidth={1.25}
          className="external-arrow shrink-0 mt-2"
          style={{ color: "var(--text-primary)" }}
        />
      </div>
    </Link>
  );
};

// -------------------- Home Page --------------------
const Home = ({ categories, stats }) => {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section
        className="relative overflow-hidden paper-grain border-b"
        style={{ borderColor: "var(--border-strong)", "--hero-map": `url(${heroMap})` }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-20 md:pb-24">
          {/* top masthead bar */}
          <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-archive mb-16 md:mb-20" style={{ color: "var(--text-secondary)" }}>
            <span>Vol. I · No. 01</span>
            <span className="hidden md:inline">
              A Companion Archive · {BOOK_TITLE}
            </span>
            <span>MMXXV</span>
          </div>

          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-9">
              <h1
                className="headline text-[13vw] md:text-[7rem] lg:text-[8.5rem]"
                data-testid="hero-title"
              >
                Canada,
                <br />
                in <em>five hundred</em>
                <br />
                voices.
              </h1>
              <div className="mt-10 md:mt-12 flex flex-col sm:flex-row gap-4 max-w-2xl">
                <Link to="/archive" className="clay-btn clay-btn-primary" data-testid="hero-cta-archive">
                  Enter the Archive
                  <ArrowUpRight size={14} strokeWidth={1.75} />
                </Link>
                <BookCTA variant="hero-secondary" data-testid="hero-cta-book" />
              </div>
            </div>
            <div
              className="col-span-12 md:col-span-3 md:pl-6 md:border-l flex flex-col justify-end"
              style={{ borderColor: "var(--border-strong)" }}
            >
              <div className="font-mono text-[10px] uppercase tracking-archive mb-3" style={{ color: "var(--text-tertiary)" }}>
                The Companion
              </div>
              <p className="font-serif text-lg md:text-xl leading-snug" style={{ color: "var(--text-primary)" }}>
                An archive for{" "}
                <em className="italic" style={{ color: "var(--accent)" }}>
                  {BOOK_TITLE}
                </em>{" "}
                by {BOOK_AUTHOR}.
              </p>
              <p className="mt-4 text-sm" style={{ color: "var(--text-secondary)" }}>
                One year. Eighteen themes. {stats?.unique_voices ?? "hundreds of"} voices — from
                Hansards to substacks to late-night YouTube monologues, catalogued and cited.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BENTO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 mt-20 md:mt-24">
        <div className="font-mono text-[10px] uppercase tracking-archive mb-6" style={{ color: "var(--text-tertiary)" }}>
          § 01 — By the Numbers
        </div>
        <div className="bento-grid" data-testid="stats-bento">
          <div className="bento-cell">
            <div className="font-mono text-[10px] uppercase tracking-archive" style={{ color: "var(--text-tertiary)" }}>
              Entries Catalogued
            </div>
            <div className="font-serif text-5xl md:text-6xl mt-3" style={{ letterSpacing: "-0.03em" }} data-testid="stat-entries">
              {stats?.total_entries ?? "…"}
            </div>
          </div>
          <div className="bento-cell">
            <div className="font-mono text-[10px] uppercase tracking-archive" style={{ color: "var(--text-tertiary)" }}>
              Thematic Sections
            </div>
            <div className="font-serif text-5xl md:text-6xl mt-3" style={{ letterSpacing: "-0.03em" }}>
              {stats?.total_categories ?? "…"}
            </div>
          </div>
          <div className="bento-cell">
            <div className="font-mono text-[10px] uppercase tracking-archive" style={{ color: "var(--text-tertiary)" }}>
              Distinct Voices
            </div>
            <div className="font-serif text-5xl md:text-6xl mt-3" style={{ letterSpacing: "-0.03em" }}>
              {stats?.unique_voices ?? "…"}
            </div>
          </div>
          <div className="bento-cell">
            <div className="font-mono text-[10px] uppercase tracking-archive" style={{ color: "var(--text-tertiary)" }}>
              Year of Record
            </div>
            <div className="font-serif text-5xl md:text-6xl mt-3" style={{ letterSpacing: "-0.03em" }}>
              {stats?.year ?? "2025"}
            </div>
          </div>
        </div>
      </section>

      {/* HOW TO BROWSE */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 mt-24 md:mt-28">
        <div className="font-mono text-[10px] uppercase tracking-archive mb-6" style={{ color: "var(--text-tertiary)" }}>
          § 02 — Two Ways to Read the Year
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link to="/archive" className="shelf-card group" data-testid="browse-by-theme">
            <div className="shelf-num">Method A</div>
            <h3 className="shelf-title mt-3 !text-5xl md:!text-6xl">By Theme</h3>
            <p className="mt-4 text-sm max-w-md" style={{ color: "var(--text-secondary)" }}>
              Follow one argument across every voice that spoke to it — the Arctic, the housing
              crisis, MAID, the tariff war, the new nationalism. Eighteen chapters.
            </p>
            <div className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-archive" style={{ color: "var(--accent)" }}>
              Browse the eighteen themes
              <ArrowUpRight size={14} strokeWidth={1.5} />
            </div>
          </Link>
          <Link to="/authors" className="shelf-card group" data-testid="browse-by-author">
            <div className="shelf-num">Method B</div>
            <h3 className="shelf-title mt-3 !text-5xl md:!text-6xl">By Author</h3>
            <p className="mt-4 text-sm max-w-md" style={{ color: "var(--text-secondary)" }}>
              Read one voice across every theme they touched. From Prime Ministers to premiers,
              essayists to elders, {stats?.unique_voices ?? "hundreds of"} names — A to Z.
            </p>
            <div className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-archive" style={{ color: "var(--accent)" }}>
              Browse the author index
              <ArrowUpRight size={14} strokeWidth={1.5} />
            </div>
          </Link>
        </div>
      </section>

      {/* CATEGORIES — LIBRARY SHELVES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 mt-24 md:mt-28">
        <div className="flex items-end justify-between border-b pb-6 mb-10" style={{ borderColor: "var(--border-strong)" }}>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-archive" style={{ color: "var(--text-tertiary)" }}>
              § 03 — Contents
            </div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3" style={{ letterSpacing: "-0.025em" }}>
              The Eighteen Themes
            </h2>
          </div>
          <Link to="/archive" className="hidden md:inline-flex clay-pill" data-testid="cta-browse-all">
            Browse all entries <ArrowUpRight size={12} strokeWidth={1.5} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {categories.map((cat, i) => (
            <Link key={cat.slug} to={`/category/${cat.slug}`} className="shelf-card" data-testid={`category-card-${cat.slug}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="shelf-num">
                  Ch. {String(i + 1).padStart(2, "0")} · {cat.count} entries
                </div>
                <ArrowUpRight size={18} strokeWidth={1.25} style={{ color: "var(--text-tertiary)" }} />
              </div>
              <h3 className="shelf-title mt-3">{cat.name}</h3>
              <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
                {cat.blurb}
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* PULLQUOTE */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 mt-32 md:mt-40">
        <div className="font-mono text-[10px] uppercase tracking-archive mb-6" style={{ color: "var(--text-tertiary)" }}>
          § 04 — An Aside
        </div>
        <blockquote className="font-serif text-3xl md:text-5xl leading-tight" style={{ letterSpacing: "-0.02em" }}>
          To read a single year of a country in full is to discover{" "}
          <span style={{ color: "var(--accent)", fontStyle: "italic" }}>
            how many countries
          </span>{" "}
          it has quietly been.
        </blockquote>
        <div className="mt-6 font-mono text-[10px] uppercase tracking-archive" style={{ color: "var(--text-tertiary)" }}>
          — From the introduction to {BOOK_TITLE}
        </div>
      </section>
    </div>
  );
};

// -------------------- Archive (library room) --------------------
const Archive = ({ categories }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("");
  const [activeType, setActiveType] = useState("");

  // whether we're in "browse the shelves" vs "reading a shelf"
  const inFilteredView = !!(activeCat || activeType || search.trim());

  useEffect(() => {
    if (!inFilteredView) {
      setEntries([]);
      return;
    }
    let active = true;
    setLoading(true);
    const params = {};
    if (activeCat) params.category = activeCat;
    if (activeType) params.source_type = activeType;
    if (search.trim()) params.q = search.trim();
    axios
      .get(`${API}/entries`, { params })
      .then((res) => {
        if (active) {
          setEntries(res.data);
          setLoading(false);
        }
      })
      .catch(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [search, activeCat, activeType, inFilteredView]);

  const sourceTypes = ["article", "video", "twitter_x", "government", "academic", "podcast"];

  return (
    <div data-testid="page-archive">
      {/* HEADER STRIP — tight, side-by-side on desktop */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-14 pb-6">
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-end">
          {/* Left: label + title */}
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-archive" style={{ color: "var(--text-tertiary)" }}>
              The Archive · Reading Room
            </div>
            <h1 className="font-serif text-5xl md:text-6xl mt-2 leading-none" style={{ letterSpacing: "-0.03em" }}>
              The Library
            </h1>
            <p className="mt-2 text-sm md:text-base" style={{ color: "var(--text-secondary)" }}>
              Eighteen shelves. Five hundred voices. One year.
            </p>
          </div>
          {/* Right: search bar */}
          <div className="relative min-w-0 md:max-w-xl md:ml-auto w-full">
            <Search
              size={18}
              strokeWidth={1.5}
              className="absolute left-5 top-1/2 -translate-y-1/2 pointer-events-none"
              style={{ color: "var(--text-tertiary)" }}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search authors, headlines, outlets…"
              className="clay-input"
              style={{ fontSize: "1rem", padding: "0.85rem 1.25rem 0.85rem 2.75rem" }}
              data-testid="search-input"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-5 top-1/2 -translate-y-1/2 font-mono text-[10px] uppercase tracking-archive"
                style={{ color: "var(--text-tertiary)" }}
                data-testid="search-clear"
              >
                clear
              </button>
            )}
          </div>
        </div>

        {/* Filter toolbar — theme + medium as elegant clay dropdowns */}
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <ClayDropdown
            label="Theme"
            value={activeCat}
            onChange={setActiveCat}
            placeholder="All Themes"
            options={categories.map((c) => ({ value: c.slug, label: c.name, count: c.count }))}
            testId="theme-dropdown"
          />
          <ClayDropdown
            label="Medium"
            value={activeType}
            onChange={setActiveType}
            placeholder="All Media"
            options={sourceTypes.map((t) => ({ value: t, label: SOURCE_LABEL[t] }))}
            testId="medium-dropdown"
          />
        </div>
      </section>

      {/* If nothing filtered, show the shelves */}
      {!inFilteredView && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 pt-6 pb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-testid="archive-shelves">
            {categories.map((cat, i) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="shelf-card"
                data-testid={`archive-shelf-${cat.slug}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="shelf-num">
                    Shelf {String(i + 1).padStart(2, "0")} · {cat.count} entries
                  </div>
                  <ArrowUpRight size={18} strokeWidth={1.25} style={{ color: "var(--text-tertiary)" }} />
                </div>
                <h3 className="shelf-title mt-3">{cat.name}</h3>
                <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
                  {cat.blurb}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* If anything filtered, show the entries list */}
      {inFilteredView && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 pt-4 pb-16">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
            <button
              type="button"
              onClick={() => {
                setActiveCat("");
                setActiveType("");
                setSearch("");
              }}
              className="font-mono text-[10px] uppercase tracking-archive underline-offset-4 hover:underline"
              style={{ color: "var(--text-tertiary)" }}
              data-testid="clear-all-filters"
            >
              Clear all filters
            </button>
            <div className="font-mono text-[10px] uppercase tracking-archive" data-testid="result-count">
              {loading ? "Searching the stacks…" : `${entries.length} entr${entries.length === 1 ? "y" : "ies"}`}
            </div>
          </div>

          {!loading && entries.length === 0 && (
            <div className="py-24 text-center font-serif text-2xl" style={{ color: "var(--text-tertiary)" }}>
              No matching voices in the archive.
            </div>
          )}

          <div data-testid="entries-list">
            {entries.map((e, i) => (
              <EntryRow key={e.id} entry={e} index={i} showCategory={!activeCat} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

// -------------------- Category Page --------------------
const CategoryPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/category/${slug}`)
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.entries;
    return data.entries.filter(
      (e) =>
        e.author.toLowerCase().includes(q) ||
        e.domain.toLowerCase().includes(q) ||
        (e.title && e.title.toLowerCase().includes(q)),
    );
  }, [data, search]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-32 font-serif text-3xl" style={{ color: "var(--text-tertiary)" }}>
        Loading…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-32">
        <div className="font-serif text-3xl">Category not found.</div>
        <Link
          to="/archive"
          className="mt-6 inline-block font-mono text-xs uppercase tracking-archive border-b pb-1"
          style={{ borderColor: "var(--text-primary)" }}
        >
          Back to archive
        </Link>
      </div>
    );
  }

  return (
    <div data-testid={`page-category-${slug}`}>
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12">
        <Link
          to="/archive"
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-archive"
          style={{ color: "var(--text-secondary)" }}
          data-testid="back-to-archive"
        >
          <ArrowLeft size={12} strokeWidth={1.5} /> Back to the Archive
        </Link>

        <div className="mt-10 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-8">
            <div className="font-mono text-[10px] uppercase tracking-archive" style={{ color: "var(--text-tertiary)" }}>
              Chapter · {data.entries.length} entries
            </div>
            <h1 className="font-serif text-5xl md:text-7xl mt-4 leading-none" style={{ letterSpacing: "-0.03em" }} data-testid="category-title">
              {data.category.name}
            </h1>
            <p className="font-serif text-2xl mt-6 max-w-2xl" style={{ color: "var(--text-secondary)" }}>
              {data.category.blurb}
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 md:pl-6 md:border-l flex flex-col justify-end" style={{ borderColor: "var(--border-strong)" }}>
            <div className="font-mono text-[10px] uppercase tracking-archive mb-3" style={{ color: "var(--text-tertiary)" }}>
              Filter within chapter
            </div>
            <div className="relative">
              <Search
                size={16}
                strokeWidth={1.5}
                className="absolute left-5 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ color: "var(--text-tertiary)" }}
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Author, headline or outlet…"
                className="clay-input"
                style={{ fontSize: "1rem", padding: "0.75rem 1.25rem 0.75rem 2.75rem" }}
                data-testid="category-search-input"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-16">
        <div className="flex items-center justify-between border-b pb-4 mb-2" style={{ borderColor: "var(--border-strong)" }}>
          <div className="font-mono text-[10px] uppercase tracking-archive" style={{ color: "var(--text-tertiary)" }}>
            Entries A–Z
          </div>
          <div className="font-mono text-[10px] uppercase tracking-archive">
            {filtered.length} of {data.entries.length}
          </div>
        </div>

        <div data-testid="category-entries-list">
          {filtered.map((e, i) => (
            <EntryRow key={e.id} entry={e} index={i} />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="py-24 text-center font-serif text-2xl" style={{ color: "var(--text-tertiary)" }}>
            No matches in this chapter.
          </div>
        )}
      </section>
    </div>
  );
};

// -------------------- Authors Index (A-Z) --------------------
// Accent-strip helper so "Étienne" groups under E, not after Z
const stripAccents = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const authorSortKey = (name) => stripAccents(name).trim().toLowerCase();

const AuthorsIndex = () => {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/authors`)
      .then((res) => {
        // Sort client-side with locale-aware, accent-insensitive comparison so
        // "Étienne" sits at E, not after Z (SQLite NOCASE doesn't fold accents).
        const sorted = [...res.data].sort((a, b) =>
          authorSortKey(a.name).localeCompare(authorSortKey(b.name), "en-CA"),
        );
        setAuthors(sorted);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = stripAccents(search.trim().toLowerCase());
    if (!q) return authors;
    return authors.filter((a) => stripAccents(a.name.toLowerCase()).includes(q));
  }, [authors, search]);

  const grouped = useMemo(() => {
    const g = {};
    for (const a of filtered) {
      const letter = stripAccents(a.name)[0]?.toUpperCase() || "#";
      const key = /[A-Z]/.test(letter) ? letter : "#";
      (g[key] = g[key] || []).push(a);
    }
    return g;
  }, [filtered]);

  const letters = Object.keys(grouped).sort();

  return (
    <div data-testid="page-authors">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12">
        <div className="font-mono text-[10px] uppercase tracking-archive" style={{ color: "var(--text-tertiary)" }}>
          The Archive · By Author
        </div>
        <h1 className="font-serif text-6xl md:text-8xl mt-4 leading-none" style={{ letterSpacing: "-0.03em" }}>
          The Author Index
        </h1>
        <p className="font-serif text-2xl mt-6 max-w-2xl" style={{ color: "var(--text-secondary)" }}>
          Every voice cited in the year of record, A to Z.{" "}
          {authors.length > 0 && `${authors.length} distinct authors.`}
        </p>

        <div className="mt-10 max-w-2xl relative">
          <Search
            size={18}
            strokeWidth={1.5}
            className="absolute left-5 top-1/2 -translate-y-1/2 pointer-events-none"
            style={{ color: "var(--text-tertiary)" }}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search authors by name…"
            className="clay-input"
            data-testid="authors-search-input"
          />
        </div>

        {/* A-Z jump */}
        {!loading && letters.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-1.5" data-testid="az-jump">
            {"ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((L) => (
              <a
                key={L}
                href={grouped[L] ? `#letter-${L}` : undefined}
                className={`az-chip ${grouped[L] ? "" : "disabled"}`}
              >
                {L}
              </a>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-16" data-testid="authors-index-list">
        {loading && (
          <div className="py-24 text-center font-serif text-2xl" style={{ color: "var(--text-tertiary)" }}>
            Loading the card catalogue…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="py-24 text-center font-serif text-2xl" style={{ color: "var(--text-tertiary)" }}>
            No authors match that search.
          </div>
        )}
        {letters.map((L) => (
          <div key={L} id={`letter-${L}`} className="mt-12 scroll-mt-24">
            <div className="flex items-center gap-4 mb-6">
              <span className="letter-block">{L}</span>
              <span className="font-mono text-[10px] uppercase tracking-archive" style={{ color: "var(--text-tertiary)" }}>
                {grouped[L].length} {grouped[L].length === 1 ? "author" : "authors"}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {grouped[L].map((a) => (
                <Link
                  key={a.slug}
                  to={`/author/${a.slug}`}
                  className="author-tile"
                  data-testid={`author-index-${a.slug}`}
                >
                  <span className="font-serif text-xl leading-tight" style={{ letterSpacing: "-0.01em" }}>
                    {a.name}
                  </span>
                  <span
                    className="font-mono text-[10px] uppercase tracking-archive shrink-0 ml-3 px-2 py-0.5 rounded-full"
                    style={{
                      color: "var(--text-tertiary)",
                      background: "rgba(139,38,38,0.08)",
                    }}
                  >
                    {a.entry_count}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
};

// -------------------- Author Page --------------------
const AuthorPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/author/${slug}`)
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-32 font-serif text-3xl" style={{ color: "var(--text-tertiary)" }}>
        Loading…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-32">
        <div className="font-serif text-3xl">Author not found.</div>
        <Link to="/authors" className="mt-6 inline-block font-mono text-xs uppercase tracking-archive border-b pb-1" style={{ borderColor: "var(--text-primary)" }}>
          Back to the author index
        </Link>
      </div>
    );
  }

  // group by category
  const byCategory = {};
  for (const e of data.entries) {
    (byCategory[e.category] = byCategory[e.category] || []).push(e);
  }
  const categoryNames = Object.keys(byCategory);

  return (
    <div data-testid={`page-author-${slug}`}>
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12">
        <Link
          to="/authors"
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-archive"
          style={{ color: "var(--text-secondary)" }}
          data-testid="back-to-authors"
        >
          <ArrowLeft size={12} strokeWidth={1.5} /> Back to the Author Index
        </Link>

        <div className="mt-10">
          <div className="font-mono text-[10px] uppercase tracking-archive" style={{ color: "var(--text-tertiary)" }}>
            {data.entries.length} {data.entries.length === 1 ? "entry" : "entries"} ·{" "}
            {categoryNames.length} {categoryNames.length === 1 ? "theme" : "themes"}
          </div>
          <h1 className="font-serif text-6xl md:text-8xl mt-4 leading-none" style={{ letterSpacing: "-0.03em" }} data-testid="author-name">
            {data.author.name}
          </h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-16" data-testid="author-entries-list">
        {categoryNames.map((catName) => (
          <div key={catName} className="mt-8">
            <div className="flex items-baseline gap-4 border-b pb-3 mb-2" style={{ borderColor: "var(--border-strong)" }}>
              <h2 className="font-serif text-2xl md:text-3xl leading-none" style={{ letterSpacing: "-0.015em" }}>
                {catName}
              </h2>
              <span className="font-mono text-[10px] uppercase tracking-archive" style={{ color: "var(--text-tertiary)" }}>
                {byCategory[catName].length} {byCategory[catName].length === 1 ? "entry" : "entries"}
              </span>
            </div>
            {byCategory[catName].map((e, i) => (
              <EntryRow key={e.id} entry={e} index={i} />
            ))}
          </div>
        ))}
      </section>
    </div>
  );
};

// -------------------- Entry Detail Page --------------------
const EmbeddedPreview = ({ entry }) => {
  const url = entry.url;
  // YouTube
  const yt =
    url.match(/(?:v=|youtu\.be\/|shorts\/|live\/)([A-Za-z0-9_-]{6,})/) ||
    url.match(/embed\/([A-Za-z0-9_-]{6,})/);
  if (yt) {
    return (
      <div className="aspect-video w-full border" style={{ borderColor: "var(--border-strong)" }}>
        <iframe
          src={`https://www.youtube.com/embed/${yt[1]}`}
          title={entry.title || entry.author}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          data-testid="embed-youtube"
        />
      </div>
    );
  }
  // X/Twitter
  if (/x\.com|twitter\.com/.test(url)) {
    return (
      <div
        className="border p-6 md:p-10 text-center"
        style={{ borderColor: "var(--border-strong)", background: "var(--surface-hover)" }}
        data-testid="embed-x"
      >
        <div className="font-mono text-[10px] uppercase tracking-archive mb-4" style={{ color: "var(--text-tertiary)" }}>
          Social Post · X (formerly Twitter)
        </div>
        <p className="font-serif text-xl italic" style={{ color: "var(--text-secondary)" }}>
          "{entry.title || "This post can't be embedded directly — X blocks previews."} "
        </p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-archive border-b pb-1"
          style={{ borderColor: "var(--text-primary)" }}
        >
          View post on X <ArrowUpRight size={14} strokeWidth={1.5} />
        </a>
      </div>
    );
  }
  // PDFs
  if (/\.pdf($|\?)/i.test(url)) {
    return (
      <div className="border" style={{ borderColor: "var(--border-strong)" }} data-testid="embed-pdf">
        <iframe src={url} title={entry.title || entry.author} className="w-full h-[720px]" />
      </div>
    );
  }
  // Article — show OG image if available
  if (entry.image_url) {
    return (
      <div className="border overflow-hidden" style={{ borderColor: "var(--border-strong)" }} data-testid="embed-image">
        <img src={entry.image_url} alt={entry.title || entry.author} className="w-full h-auto block" loading="lazy" />
      </div>
    );
  }
  return null;
};

const EntryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [siblings, setSiblings] = useState(null); // { prev, next, position, total }

  useEffect(() => {
    setLoading(true);
    setSiblings(null);
    window.scrollTo(0, 0);
    axios
      .get(`${API}/entry/${id}`)
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  // Fetch category siblings for prev/next navigation
  useEffect(() => {
    if (!data?.entry?.category_slug) return;
    let active = true;
    axios
      .get(`${API}/category/${data.entry.category_slug}`)
      .then((res) => {
        if (!active) return;
        const list = res.data?.entries || [];
        const idx = list.findIndex((e) => String(e.id) === String(id));
        if (idx === -1) {
          setSiblings({ prev: null, next: null, position: 0, total: list.length });
          return;
        }
        setSiblings({
          prev: idx > 0 ? list[idx - 1] : null,
          next: idx < list.length - 1 ? list[idx + 1] : null,
          position: idx + 1,
          total: list.length,
        });
      })
      .catch(() => active && setSiblings({ prev: null, next: null, position: 0, total: 0 }));
    return () => {
      active = false;
    };
  }, [data, id]);

  // Keyboard navigation: ← / →
  useEffect(() => {
    if (!siblings) return;
    const handler = (e) => {
      // Ignore when user is typing in an input/textarea
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key === "ArrowLeft" && siblings.prev) {
        navigate(`/entry/${siblings.prev.id}`);
      } else if (e.key === "ArrowRight" && siblings.next) {
        navigate(`/entry/${siblings.next.id}`);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [siblings, navigate]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-32 font-serif text-3xl" style={{ color: "var(--text-tertiary)" }}>
        Loading…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-32">
        <div className="font-serif text-3xl">Entry not found.</div>
      </div>
    );
  }
  const e = data.entry;
  const Icon = SOURCE_ICON[e.source_type] || FileText;
  const pubDate = formatDate(e.published_at);
  const prev = siblings?.prev;
  const next = siblings?.next;

  return (
    <div data-testid={`page-entry-${id}`} className="relative">
      {/* Floating side arrows — desktop only */}
      <button
        type="button"
        onClick={() => prev && navigate(`/entry/${prev.id}`)}
        disabled={!prev}
        aria-label={prev ? `Previous entry: ${prev.title || prev.author}` : "No previous entry"}
        title={prev ? `Previous: ${prev.title || prev.author}` : "No previous entry"}
        className="clay-arrow hidden xl:flex fixed left-6 top-1/2 -translate-y-1/2 z-40"
        data-testid="entry-prev-side"
      >
        <ArrowLeft size={18} strokeWidth={1.75} />
      </button>
      <button
        type="button"
        onClick={() => next && navigate(`/entry/${next.id}`)}
        disabled={!next}
        aria-label={next ? `Next entry: ${next.title || next.author}` : "No next entry"}
        title={next ? `Next: ${next.title || next.author}` : "No next entry"}
        className="clay-arrow hidden xl:flex fixed right-6 top-1/2 -translate-y-1/2 z-40"
        data-testid="entry-next-side"
      >
        <ArrowRight size={18} strokeWidth={1.75} />
      </button>

      <article className="max-w-4xl mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-16">
        <div className="flex items-center justify-between gap-4">
          <Link
            to={`/author/${e.author_slug}`}
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-archive"
            style={{ color: "var(--text-secondary)" }}
            data-testid="back-to-author"
          >
            <ArrowLeft size={12} strokeWidth={1.5} /> {e.author}
          </Link>
          {/* Inline prev/next — visible on tablet + mobile (arrows are hidden < xl) */}
          <div className="flex items-center gap-2 xl:hidden">
            <button
              type="button"
              onClick={() => prev && navigate(`/entry/${prev.id}`)}
              disabled={!prev}
              aria-label="Previous entry"
              className="clay-arrow !w-10 !h-10"
              data-testid="entry-prev-top"
            >
              <ArrowLeft size={16} strokeWidth={1.75} />
            </button>
            <button
              type="button"
              onClick={() => next && navigate(`/entry/${next.id}`)}
              disabled={!next}
              aria-label="Next entry"
              className="clay-arrow !w-10 !h-10"
              data-testid="entry-next-top"
            >
              <ArrowRight size={16} strokeWidth={1.75} />
            </button>
          </div>
        </div>

        {/* Meta strip */}
        <div className="mt-8 flex flex-wrap items-center gap-3 text-xs font-mono uppercase tracking-archive" style={{ color: "var(--text-tertiary)" }}>
          <Link to={`/category/${e.category_slug}`} className="hover:text-[var(--text-primary)] transition-colors">
            {e.category}
          </Link>
          <span>·</span>
          <span className="inline-flex items-center gap-1.5">
            <Icon size={11} strokeWidth={1.5} /> {SOURCE_LABEL[e.source_type]}
          </span>
          <span>·</span>
          <span>{e.outlet_name || e.domain}</span>
          {pubDate && (
            <>
              <span>·</span>
              <span>{pubDate}</span>
            </>
          )}
        </div>

        {/* Headline */}
        <h1
          className="font-serif text-5xl md:text-7xl mt-6 leading-[0.95]"
          style={{ letterSpacing: "-0.025em" }}
          data-testid="entry-title"
        >
          {e.title || `Untitled — ${e.author} in ${e.outlet_name || e.domain}`}
        </h1>

        {/* Byline */}
        <div className="mt-8 flex items-baseline gap-4 border-y py-4" style={{ borderColor: "var(--border-strong)" }}>
          <div className="font-mono text-[10px] uppercase tracking-archive" style={{ color: "var(--text-tertiary)" }}>
            By
          </div>
          <Link
            to={`/author/${e.author_slug}`}
            className="font-serif text-2xl italic"
            style={{ letterSpacing: "-0.01em" }}
          >
            {e.author}
          </Link>
        </div>

        {/* Description */}
        {e.description && (
          <p className="font-serif text-2xl md:text-[26px] leading-snug mt-8" style={{ color: "var(--text-primary)" }}>
            {e.description}
          </p>
        )}

        {/* Pull quote */}
        {e.pull_quote && (
          <blockquote
            className="my-12 pl-6 border-l-4 font-serif text-2xl italic leading-relaxed"
            style={{ borderColor: "var(--accent)" }}
          >
            {e.pull_quote}
          </blockquote>
        )}

        {/* Embed / preview */}
        <div className="mt-10">
          <EmbeddedPreview entry={e} />
        </div>

        {/* Read the original */}
        <div className="mt-10 shelf-card !p-8 md:!p-10">
          <div className="font-mono text-[10px] uppercase tracking-archive mb-4" style={{ color: "var(--text-tertiary)" }}>
            Read the full piece at the source
          </div>
          <a
            href={e.url}
            target="_blank"
            rel="noopener noreferrer"
            className="clay-btn clay-btn-primary"
            data-testid="entry-external-link"
          >
            <ExternalLink size={14} strokeWidth={1.75} />
            <span>Open at {e.outlet_name || e.domain}</span>
            <ArrowUpRight size={12} strokeWidth={1.75} />
          </a>
          <div className="mt-5 text-xs font-mono" style={{ color: "var(--text-tertiary)" }}>
            The 2025 Canada Index only catalogues sources; copyright belongs to the original publisher.
          </div>
        </div>

        {/* Related */}
        {data.related_by_author?.length > 0 && (
          <div className="mt-16">
            <div className="font-mono text-[10px] uppercase tracking-archive mb-3" style={{ color: "var(--text-tertiary)" }}>
              More from {e.author}
            </div>
            {data.related_by_author.map((r, i) => (
              <EntryRow key={r.id} entry={r} index={i} showCategory />
            ))}
          </div>
        )}
        {data.related_by_category?.length > 0 && (
          <div className="mt-12">
            <div className="font-mono text-[10px] uppercase tracking-archive mb-3" style={{ color: "var(--text-tertiary)" }}>
              Elsewhere in {e.category}
            </div>
            {data.related_by_category.map((r, i) => (
              <EntryRow key={r.id} entry={r} index={i} />
            ))}
          </div>
        )}

        {/* Page-flip navigation — prev / next within this theme */}
        {(prev || next) && (
          <nav
            className="mt-20 pt-8 border-t"
            style={{ borderColor: "var(--border-strong)" }}
            data-testid="entry-page-flip"
          >
            <div className="font-mono text-[10px] uppercase tracking-archive mb-5 flex items-center justify-between" style={{ color: "var(--text-tertiary)" }}>
              <span>Flip through {e.category}</span>
              {siblings?.total > 0 && (
                <span>
                  {siblings.position} / {siblings.total}
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {prev ? (
                <Link
                  to={`/entry/${prev.id}`}
                  className="shelf-card !p-6 group"
                  data-testid="entry-prev-card"
                >
                  <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-archive" style={{ color: "var(--accent)" }}>
                    <ArrowLeft size={14} strokeWidth={1.5} /> Previous
                  </div>
                  <div className="font-serif text-xl md:text-2xl mt-3 leading-tight" style={{ letterSpacing: "-0.015em" }}>
                    {prev.title || `${prev.author} · ${prev.outlet_name || prev.domain}`}
                  </div>
                  <div className="mt-2 text-sm italic" style={{ color: "var(--text-secondary)" }}>
                    {prev.author}
                  </div>
                </Link>
              ) : (
                <div />
              )}
              {next ? (
                <Link
                  to={`/entry/${next.id}`}
                  className="shelf-card !p-6 group md:text-right"
                  data-testid="entry-next-card"
                >
                  <div className="flex items-center md:justify-end gap-3 font-mono text-[10px] uppercase tracking-archive" style={{ color: "var(--accent)" }}>
                    Next <ArrowRight size={14} strokeWidth={1.5} />
                  </div>
                  <div className="font-serif text-xl md:text-2xl mt-3 leading-tight" style={{ letterSpacing: "-0.015em" }}>
                    {next.title || `${next.author} · ${next.outlet_name || next.domain}`}
                  </div>
                  <div className="mt-2 text-sm italic" style={{ color: "var(--text-secondary)" }}>
                    {next.author}
                  </div>
                </Link>
              ) : (
                <div />
              )}
            </div>
            <div className="mt-4 font-mono text-[10px] uppercase tracking-archive text-center" style={{ color: "var(--text-tertiary)" }}>
              Tip: use ← / → to flip
            </div>
          </nav>
        )}
      </article>
    </div>
  );
};

// -------------------- About --------------------
const About = () => (
  <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="page-about">
    <div className="font-mono text-[10px] uppercase tracking-archive" style={{ color: "var(--text-tertiary)" }}>
      Colophon
    </div>
    <h1 className="font-serif text-6xl md:text-7xl mt-4 leading-none" style={{ letterSpacing: "-0.03em" }}>
      About this archive
    </h1>
    <div className="mt-10 space-y-6 font-serif text-xl leading-relaxed" style={{ color: "var(--text-secondary)" }}>
      <p>
        <span style={{ color: "var(--text-primary)" }}>The 2025 Canada Index</span> is the
        companion archive to{" "}
        <a
          href={BOOK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="italic underline underline-offset-2"
          style={{ color: "var(--text-primary)" }}
        >
          {BOOK_TITLE}
        </a>{" "}
        by {BOOK_AUTHOR} — an attempt to register, in one place, the argument Canada had with
        itself in a single year.
      </p>
      <p>
        Sources include newspapers of record, Hansard transcripts, broadcasts, podcasts,
        peer-reviewed journals, government reports, op-eds, and the unruly archive of social
        media. Inclusion is not endorsement. Some of these voices contradict each other on every
        page — that is the point.
      </p>
      <p>
        Entries are organised two ways: by <Link to="/archive" className="underline">theme</Link>{" "}
        (eighteen chapters) and by{" "}
        <Link to="/authors" className="underline">author</Link> (A–Z). Each entry links to the
        original source; where the outlet permits, a preview shows the headline, description, and
        hero image. To read the full piece, click through to the original publisher.
      </p>
      <p>
        <a
          href={BOOK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="italic underline underline-offset-2"
          style={{ color: "var(--text-primary)" }}
        >
          {BOOK_TITLE}
        </a>{" "}
        weaves these voices together into a single narrative. The book is available now from{" "}
        <a
          href={BOOK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="italic underline underline-offset-2"
          style={{ color: "var(--text-primary)" }}
        >
          Friesen Press
        </a>
        .
      </p>
    </div>
    <div className="mt-12">
      <BookCTA variant="hero-primary" data-testid="about-book-cta" />
    </div>
    <div className="mt-16 pt-10 border-t font-mono text-[10px] uppercase tracking-archive" style={{ borderColor: "var(--border-subtle)", color: "var(--text-tertiary)" }}>
      <div className="grid grid-cols-2 gap-y-3">
        <span>Typography</span>
        <span style={{ color: "var(--text-primary)" }}>Cormorant Garamond · Work Sans · IBM Plex Mono</span>
        <span>Palette</span>
        <span style={{ color: "var(--text-primary)" }}>Paper white · Maple red · Ink black</span>
        <span>Data</span>
        <span style={{ color: "var(--text-primary)" }}>Turso (libSQL) on Cloudflare Workers</span>
        <span>Companion to</span>
        <span style={{ color: "var(--text-primary)" }}>
          <a
            href={BOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="italic underline underline-offset-2"
          >
            {BOOK_TITLE}
          </a>
        </span>
      </div>
    </div>
  </div>
);

// -------------------- App Shell --------------------
function Shell() {
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/categories`).then((res) => setCategories(res.data)).catch(() => {});
    axios.get(`${API}/stats`).then((res) => setStats(res.data)).catch(() => {});
  }, []);

  return (
    <>
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<Home categories={categories} stats={stats} />} />
          <Route path="/archive" element={<Archive categories={categories} />} />
          <Route path="/category/:slug" element={<CategoryPage />} />
          <Route path="/authors" element={<AuthorsIndex />} />
          <Route path="/author/:slug" element={<AuthorPage />} />
          <Route path="/entry/:id" element={<EntryPage />} />
          <Route path="/about" element={<About />} />
        </Routes>
      </main>
      <Footer stats={stats} />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </div>
  );
}

export default App;
